/* SyncNowButton — manual eZee sync trigger (Bundle 2.9 · 5 May 2026).
 * Fires POST /api/daily-sales/sync-now/{division_id}, polls
 * /sync-status/{division_id} until the in_flight flag clears, then
 * calls onComplete() so the caller can refresh whichever tile it owns.
 *
 * Visible to CEO + leader + sub_leader + cost_controller (the API
 * gates on the same set of roles). Renders nothing if the user
 * can't reach it — the parent decides when to show.
 *
 * Why this exists: Dipali doctrine 5 May 2026 — "the LIVE badge isn't
 * showing live as of now in all outlets." The intra-day cron runs
 * every 30 min, but when the floor manager just rang an order and
 * wants to confirm it landed, they shouldn't have to wait. One click,
 * eZee re-pulled, dashboard refreshed.
 */
import React, { useEffect, useState, useCallback } from 'react';
import api from '../lib/api';
import { RefreshCw, CheckCircle2, AlertCircle } from 'lucide-react';

const fmtAge = (s) => {
  if (s == null) return '—';
  if (s < 60) return `${Math.round(s)}s ago`;
  if (s < 3600) return `${Math.round(s / 60)} min ago`;
  if (s < 86400) return `${Math.round(s / 3600)} h ago`;
  return `${Math.round(s / 86400)} d ago`;
};

export const SyncNowButton = ({
  divisionId = 'me',
  variant = 'light',           // 'light' for white BGs, 'dark' for navy ticker
  onComplete,                  // callback after a successful sync
  testid = 'sync-now-btn',
}) => {
  const [status, setStatus] = useState('idle'); // idle | syncing | done | error
  const [age, setAge] = useState(null);
  const [error, setError] = useState(null);

  const palette = variant === 'dark'
    ? {
        bg: 'rgba(255,255,255,.08)', bgHover: 'rgba(255,255,255,.16)',
        fg: '#CBDCEC', fgActive: '#34D399', err: '#FCA5A5', sub: '#9DB5CC',
      }
    : {
        bg: '#F7F5F2', bgHover: '#EFEAE2',
        fg: '#1B2A4A', fgActive: '#10B981', err: '#DC2626', sub: '#6B7280',
      };

  const fetchAge = useCallback(async () => {
    try {
      const r = await api.get(`/daily-sales/sync-status/${divisionId}`);
      const ezee = (r.data?.sources || []).find(s => s.id === 'ezee_sales');
      if (ezee?.age_seconds != null) setAge(ezee.age_seconds);
    } catch {
      /* keep last known age */
    }
  }, [divisionId]);

  // Fetch age on mount and every 60s while idle
  useEffect(() => {
    fetchAge();
    const iv = setInterval(fetchAge, 60_000);
    return () => clearInterval(iv);
  }, [fetchAge]);

  const handleClick = async () => {
    if (status === 'syncing') return;
    setStatus('syncing');
    setError(null);
    try {
      await api.post(`/daily-sales/sync-now/${divisionId}`);
      // Poll sync-status every 2s up to 90s — heavy fetch can take 30-60s
      const start = Date.now();
      let landed = false;
      while (Date.now() - start < 90_000) {
        await new Promise(r => setTimeout(r, 2000));
        try {
          const r = await api.get(`/daily-sales/sync-status/${divisionId}`);
          const ezee = (r.data?.sources || []).find(s => s.id === 'ezee_sales');
          if (ezee && ezee.in_flight === false &&
              ezee.age_seconds != null && ezee.age_seconds < 60) {
            landed = true;
            setAge(ezee.age_seconds);
            break;
          }
        } catch { /* swallow & retry */ }
      }
      setStatus(landed ? 'done' : 'error');
      if (!landed) setError('Sync started but didn\'t finish in 90s. The cron may still complete in the background.');
      if (landed && onComplete) onComplete();
      // Reset status badge after 3s
      setTimeout(() => setStatus('idle'), 3000);
    } catch (e) {
      setStatus('error');
      setError(e?.response?.data?.detail || e?.message || 'Sync failed');
      setTimeout(() => setStatus('idle'), 4000);
    }
  };

  const Icon = status === 'done' ? CheckCircle2
             : status === 'error' ? AlertCircle
             : RefreshCw;
  const fg = status === 'done' ? palette.fgActive
           : status === 'error' ? palette.err
           : palette.fg;
  const label = status === 'syncing' ? 'Syncing eZee…'
              : status === 'done'    ? 'Synced'
              : status === 'error'   ? 'Sync failed'
              : 'Sync now';

  return (
    <div className="inline-flex items-center gap-2" data-testid={`${testid}-wrap`}>
      <button
        onClick={handleClick}
        disabled={status === 'syncing'}
        title={error || `Force-pull today's sales from eZee · last synced ${fmtAge(age)}`}
        data-testid={testid}
        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-medium transition-colors"
        style={{
          backgroundColor: palette.bg,
          color: fg,
          cursor: status === 'syncing' ? 'wait' : 'pointer',
          opacity: status === 'syncing' ? 0.85 : 1,
        }}
        onMouseEnter={(e) => { if (status !== 'syncing') e.currentTarget.style.backgroundColor = palette.bgHover; }}
        onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = palette.bg; }}
      >
        <Icon size={12} className={status === 'syncing' ? 'animate-spin' : ''} />
        {label}
      </button>
      <span className="text-[10px]" style={{ color: palette.sub }} data-testid={`${testid}-age`}>
        last synced {fmtAge(age)}
      </span>
    </div>
  );
};

export default SyncNowButton;
